import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEO from '@/components/feature/SEO';
import ArticleCard from '@/components/base/ArticleCard';
import { getArticleById, getArticlesByCategory, getHotArticles } from '@/mocks/articles';
import NotFound from '@/pages/NotFound';

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;

  if (!article) {
    return <NotFound />;
  }

  const relatedArticles = getArticlesByCategory(article.category)
    .filter((a) => a.id !== article.id)
    .slice(0, 3);

  if (relatedArticles.length === 0) {
    const others = getHotArticles(3).filter((a) => a.id !== article.id);
    relatedArticles.push(...others.slice(0, 3));
  }

  const baseUrl = 'https://fulidaohang.com';

  const articleSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: article.title,
        description: article.summary,
        datePublished: article.date,
        author: {
          '@type': 'Organization',
          name: '福利导航',
        },
        publisher: {
          '@type': 'Organization',
          name: '福利导航',
          url: baseUrl,
        },
        url: `${baseUrl}/article/${article.id}`,
        image: article.imageUrl,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: '首页',
            item: baseUrl,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: article.categoryName,
            item: `${baseUrl}/category/${article.category}`,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: article.title,
          },
        ],
      },
    ],
  };

  return (
    <>
      <SEO
        title={`${article.title} - 福利导航`}
        description={article.summary}
        keywords={article.tags.join(',')}
        canonicalUrl={`/article/${article.id}`}
        ogType="article"
        ogImage={article.imageUrl}
        schema={articleSchema}
      />
      <Navbar />
      <main className="pt-16">
        <article>
          <div className="bg-background-50 border-b border-background-200/60">
            <div className="w-full max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
              <Breadcrumb
                items={[
                  { label: article.categoryName, to: `/category/${article.category}` },
                  { label: article.title },
                ]}
              />
              <h1 className="text-2xl md:text-4xl font-heading text-foreground-900 mt-6 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center flex-wrap gap-4 text-sm text-foreground-500">
                <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs">
                  {article.categoryName}
                </span>
                <span>{article.date}</span>
                <div className="flex items-center flex-wrap gap-1.5">
                  {article.tags.map((tag) => (
                    <span key={tag} className="text-xs px-2 py-0.5 rounded bg-background-100 text-foreground-500">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="w-full max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
            <div className="w-full aspect-[16/9] rounded-lg overflow-hidden mb-8 md:mb-10">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
                prose-h2:text-xl prose-h2:md:text-2xl prose-h2:font-heading prose-h2:text-foreground-900 prose-h2:mt-8 prose-h2:mb-4
                prose-h3:text-lg prose-h3:font-heading prose-h3:text-foreground-900 prose-h3:mt-6 prose-h3:mb-3
                prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
                prose-strong:text-foreground-900 prose-strong:font-medium
                prose-li:text-foreground-700 prose-li:mb-1.5
                prose-ul:my-4 prose-ol:my-4
                prose-ul:pl-5 prose-ol:pl-5"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-12 pt-8 border-t border-background-200/60">
              <div className="flex items-center flex-wrap gap-3">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span key={tag} className="text-xs px-3 py-1.5 rounded-full bg-background-100 text-foreground-600">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full bg-background-100 py-12 md:py-16">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <h3 className="text-xl md:text-2xl font-heading text-foreground-900 mb-8">
                相关福利攻略
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((a) => (
                  <ArticleCard key={a.id} article={a} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}